import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import NotFoundPage from "./NotFoundPage";
import {
    formatSpawningMonths,
    formatBiteWind,
    formatBiteWindDirection,
    formatBitePrecipitation,
    formatBiteCloudiness,
    formatBitePreasure,
    formatBiteMoon,
    formatPlaceFlow,
    formatPlaceShadow,
    formatTimeActiveBite,
    formatTempRange,
    formatDepthRange,
} from "../utils/fishLabelsRu";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

function photoSrc(photo) {
    if (!photo) return null;
    if (photo.startsWith("http")) return photo;
    const path = photo.includes("/") ? photo : `uploads/images/${photo}`;
    return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

function FishFactHeader({ label }) {
    return (
        <tr className="fish-info-header">
            <td>{label}</td>
            <td></td>
        </tr>
    );
}

function FishFact({ text, label }) {
    return (
        <tr className="fish-item">
            <td>{label}</td>
            <td className="fish-item-bold">{text}</td>
        </tr>
    );
}

function FishTable({ fish }) {
    const labelLang = "ru";
    const windPart = formatBiteWind(fish.biteWind, labelLang);
    const dirPart = formatBiteWindDirection(fish.biteWindDirection, labelLang);
    const windLine = dirPart ? `${windPart} / ${dirPart}` : windPart;
    return (
        <>
            <table className="fish-info">
                <tbody>
                    <FishFactHeader label="Описание" />
                    <tr className="fish-item">
                        <td colSpan={2} dangerouslySetInnerHTML={{ __html: fish.description ? fish.description : fish.shortDescription }} />
                    </tr>
                </tbody>
            </table>
            <table className="fish-info fish-info-additional-data">
                <tbody>
                    <FishFactHeader label="Общая информация" />
                    <FishFact text={formatSpawningMonths(fish.spawningMonth, labelLang) || "—"} label="Месяцы нереста" />
                    <FishFact text={fish.isPredator ? "Да" : "Нет"} label="Хищная рыба" />
                    <FishFact text={formatTempRange(fish.spawningTemperatureStart, fish.spawningTemperatureEnd)} label="Температура нереста" />
                    <FishFact text={formatTempRange(fish.biteTempStart, fish.biteTempEnd)} label="Температура клёва" />
                    <FishFact text={windLine} label="Ветер / Направление" />
                    <FishFact text={formatBitePrecipitation(fish.bitePrecipitation, labelLang) || "—"} label="Осадки" />
                    <FishFact text={formatBiteCloudiness(fish.biteCloudiness, labelLang) || "—"} label="Облачность" />
                    <FishFact text={formatBitePreasure(fish.bitePreasure, labelLang) || "—"} label="Давление" />
                    <FishFact text={formatBiteMoon(fish.biteMoon, labelLang) || "—"} label="Фаза Луны" />
                    <FishFact text={formatPlaceFlow(fish.placeFlow, labelLang) || "—"} label="Течение" />
                    <FishFact text={formatDepthRange(fish.placeDeepStart, fish.placeDeepEnd)} label="Глубины" />
                    <FishFact text={formatPlaceShadow(fish.placeShadow, labelLang) || "—"} label="Тенистость" />
                    <FishFact text={formatTimeActiveBite(fish.timeActiveBite, labelLang) || "—"} label="Время активного клёва" />
                </tbody>
            </table>
        </>
    );
}

export default function FishDetailPage() {
    const { slug } = useParams();
    const [fish, setFish] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        setError(null);
        axios
            .get(`${API_BASE_URL}/api/fish/${slug}`, { params: { countView: true } })
            .then((response) => setFish(response.data))
            .catch((err) => {
                if (err?.response?.status === 404) {
                    setNotFound(true);
                    setFish(null);
                    return;
                }
                setError("Не удалось загрузить рыбу.");
            })
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return (
            <div className="cms-page-root">
                <div className="cms-page-inner fish-detail-inner">
                    <article className="cms-page-panel">
                        <p className="cms-page-loading">Загрузка...</p>
                    </article>
                </div>
            </div>
        );
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (error || !fish) {
        return (
            <div className="cms-page-root">
                <div className="cms-page-inner fish-detail-inner">
                    <article className="cms-page-panel">
                        <p className="cms-page-loading">{error || "Рыба не найдена."}</p>
                        <Link to="/fishes" className="btn-outline back-to-button">
                            ← К каталогу
                        </Link>
                    </article>
                </div>
            </div>
        );
    }

    const img = photoSrc(fish.photo);

    return (
        <div className="cms-page-root">
            <div className="cms-page-inner fish-detail-inner">
                <article className="cms-page-panel fish-detail-panel">
                    <h1 className="fish-detail-title">{fish.name}</h1>

                    <div className="fish-detail__layout">
                        <div className="fish-detail__image-col">
                            {img ? (
                                <img
                                    className="fish-detail__image"
                                    src={img}
                                    alt={fish.name}
                                    width={600}
                                    height={450}
                                    decoding="async"
                                />
                            ) : (
                                <div className="fish-detail__no-image">
                                    <span>Фото недоступно</span>
                                </div>
                            )}
                            <span className={`fish-glass-card__badge fish-detail-badge${fish.isPredator ? " fish-glass-card__badge--predator" : " fish-glass-card__badge--peaceful"}`}>
                                {fish.isPredator ? "Хищная" : "Мирная"}
                            </span>
                        </div>
                        <div className="fish-detail__content">
                            <FishTable fish={fish} />
                        </div>
                    </div>
                </article>
            </div>
        </div>
    );
}
